import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Music, Pause, Play, X } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { useAmbiencePreferences } from '@/hooks/useAmbiencePreferences';
import {
  getAmbienceEngine,
  isYouTubeStyle,
  STYLE_META,
  YOUTUBE_BACKED,
} from '@/lib/ambience';

// ─────────────────────────────────────────────────────────────────────────────
// AmbientPlayer — floating music widget, bottom-right of all dashboard pages.
//
// Three audio sources are supported:
//   1. Hosted MP3  (piano / nature)         — HTMLAudioElement from Supabase Storage
//   2. Web Audio synth                       — AmbienceEngine fallback drone
//   3. YouTube IFrame                        — forest / vivaldi streamed from YouTube
//
// For YouTube styles a mini video embed is always visible in the widget so
// audio keeps playing even when the controls pill is collapsed, and to stay
// compliant with YouTube's embedding policy (player must be visible).
// ─────────────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const AmbientPlayer: React.FC = () => {
  const { prefs, update, loading } = useAmbiencePreferences();
  const [expanded, setExpanded] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  // YouTube player state
  const ytWrapRef        = useRef<HTMLDivElement>(null);
  const ytPlayerRef      = useRef<any>(null);
  const ytActiveVideoRef = useRef<string | null>(null);
  const ytApiReadyRef    = useRef(false);

  // ── Engine subscription ──────────────────────────────────────────────────
  const engineState = useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined') return () => {};
      return getAmbienceEngine().subscribe(cb);
    },
    () => {
      if (typeof window === 'undefined')
        return { isPlaying: false, style: 'silence' as const, volume: 0.3 };
      return getAmbienceEngine().getState();
    },
    () => ({ isPlaying: false, style: 'silence' as const, volume: 0.3 }),
  );

  const isYtActive = isYouTubeStyle(prefs.style);
  const ytVideoId  = YOUTUBE_BACKED[prefs.style] ?? null;

  // ── YouTube player helpers ───────────────────────────────────────────────

  /** Create (or re-create) the YT.Player inside ytWrapRef. */
  const mountYtPlayer = useCallback(
    (videoId: string, autoplay: boolean) => {
      if (!ytWrapRef.current) return;

      // If same video is already mounted, just sync play state
      if (ytPlayerRef.current && ytActiveVideoRef.current === videoId) {
        try {
          if (autoplay) ytPlayerRef.current.playVideo?.();
          else          ytPlayerRef.current.pauseVideo?.();
        } catch {}
        return;
      }

      // Destroy old player
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch {}
        ytPlayerRef.current = null;
      }

      // Clear wrapper and add a fresh target div
      ytWrapRef.current.innerHTML = '';
      const target = document.createElement('div');
      ytWrapRef.current.appendChild(target);

      ytActiveVideoRef.current = videoId;
      ytPlayerRef.current = new window.YT.Player(target, {
        videoId,
        width:  '100%',
        height: '100%',
        playerVars: {
          autoplay:      autoplay ? 1 : 0,
          loop:          1,
          playlist:      videoId,   // required for loop=1
          controls:      0,
          modestbranding:1,
          rel:           0,
          iv_load_policy:3,
          fs:            0,
          disablekb:     1,
        },
        events: {
          onReady: (e: any) => {
            e.target.setVolume(Math.round(prefs.volume * 100));
            if (autoplay) e.target.playVideo();
          },
          onError: () => {
            console.warn('YouTube ambient player error');
            // Fall back gracefully — engine stays in YouTube style but
            // audio just won't play; user can switch to another style.
          },
        },
      });
    },
    [prefs.volume],
  );

  /** Load the YouTube IFrame API script once, then call mountYtPlayer. */
  const ensureYtApi = useCallback(
    (videoId: string, autoplay: boolean) => {
      if (typeof window === 'undefined') return;

      const run = () => mountYtPlayer(videoId, autoplay);

      if (window.YT?.Player) {
        ytApiReadyRef.current = true;
        run();
        return;
      }

      // Script already injected (loading in progress)
      if (document.getElementById('yt-iframe-api-script')) {
        // Poll until the API resolves
        const iv = setInterval(() => {
          if (window.YT?.Player) { clearInterval(iv); ytApiReadyRef.current = true; run(); }
        }, 150);
        return;
      }

      // Inject script for the first time
      window.onYouTubeIframeAPIReady = () => {
        ytApiReadyRef.current = true;
        run();
      };
      const s = document.createElement('script');
      s.id  = 'yt-iframe-api-script';
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    },
    [mountYtPlayer],
  );

  // ── Effects ──────────────────────────────────────────────────────────────

  // Initialize / tear down YouTube player when style changes
  useEffect(() => {
    if (!isYtActive || !ytVideoId) {
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch {}
        ytPlayerRef.current      = null;
        ytActiveVideoRef.current = null;
      }
      return;
    }
    ensureYtApi(ytVideoId, engineState.isPlaying);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYtActive, ytVideoId]);

  // Sync engine play/pause state → YouTube player
  useEffect(() => {
    if (!ytPlayerRef.current || !isYtActive) return;
    try {
      if (engineState.isPlaying) ytPlayerRef.current.playVideo?.();
      else                       ytPlayerRef.current.pauseVideo?.();
    } catch {}
  }, [engineState.isPlaying, isYtActive]);

  // Sync volume → YouTube player
  useEffect(() => {
    if (!ytPlayerRef.current) return;
    try { ytPlayerRef.current.setVolume?.(Math.round(prefs.volume * 100)); } catch {}
  }, [prefs.volume]);

  // Honor stored preferences on load
  useEffect(() => {
    if (loading || typeof window === 'undefined') return;
    const engine = getAmbienceEngine();
    engine.setVolume(prefs.volume);
    if (prefs.enabled && !engineState.isPlaying) {
      engine.play(prefs.style).then((started) => setAutoplayBlocked(!started));
    }
    if (!prefs.enabled && engineState.isPlaying) engine.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, prefs.enabled, prefs.style]);

  // Destroy YouTube player on component unmount
  useEffect(() => {
    return () => {
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch {}
        ytPlayerRef.current = null;
      }
    };
  }, []);

  // ── Event handlers ───────────────────────────────────────────────────────

  const togglePlay = async () => {
    const engine = getAmbienceEngine();
    if (engineState.isPlaying) {
      engine.pause();
      await update({ enabled: false });
    } else {
      const started = await engine.play(prefs.style);
      setAutoplayBlocked(!started);
      if (started) await update({ enabled: true });
    }
  };

  const handleVolume = (v: number) => {
    getAmbienceEngine().setVolume(v);
    update({ volume: v });
  };

  const handleStyleChange = async (style: typeof prefs.style) => {
    await update({ style });
    if (engineState.isPlaying) {
      const started = await getAmbienceEngine().play(style);
      setAutoplayBlocked(!started);
    }
  };

  if (loading) return null;

  const isPlaying = engineState.isPlaying;
  const trackName = STYLE_META[prefs.style]?.trackName ?? 'Ambient';

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">

      {/* ── Controls pill (shown when expanded, any style) ───────────── */}
      {expanded && (
        <div className="bg-background/95 backdrop-blur border border-border/60 rounded-full shadow-lg pl-2 pr-3 py-1.5 flex items-center gap-3 min-w-[280px]">
          <button
            onClick={togglePlay}
            className="rounded-full p-1.5 hover:bg-muted transition-colors"
            aria-label={isPlaying ? 'Pause ambient music' : 'Play ambient music'}
          >
            {isPlaying
              ? <Pause className="h-3.5 w-3.5" />
              : <Play  className="h-3.5 w-3.5" />}
          </button>

          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium truncate">
              {autoplayBlocked && !isPlaying
                ? 'Click play to start'
                : isPlaying
                  ? trackName
                  : 'Paused'}
            </div>
            <select
              value={prefs.style}
              onChange={(e) => handleStyleChange(e.target.value as typeof prefs.style)}
              className="bg-transparent text-[10px] text-muted-foreground -ml-1 pr-2 py-0 cursor-pointer focus:outline-none"
            >
              {(Object.keys(STYLE_META) as Array<keyof typeof STYLE_META>).map((s) => (
                <option key={s} value={s}>{STYLE_META[s].label}</option>
              ))}
            </select>
          </div>

          <div className="w-20">
            <Slider
              min={0} max={1} step={0.05}
              value={[prefs.volume]}
              onValueChange={(v) => handleVolume(v[0] ?? 0)}
            />
          </div>

          <button
            onClick={() => setExpanded(false)}
            className="rounded-full p-1 hover:bg-muted transition-colors"
            aria-label="Collapse player"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* ── YouTube mini-player ──────────────────────────────────────────
          Always in the DOM when a YouTube style is active so audio keeps
          playing even when the controls pill is collapsed. Clicking it
          toggles the controls open/closed.
      ─────────────────────────────────────────────────────────────────── */}
      <div
        className="relative rounded-xl overflow-hidden shadow-lg border border-border/60 bg-black"
        style={{
          width:   220,
          height:  124,
          display: isYtActive ? 'block' : 'none',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
        title={isPlaying ? `Playing: ${trackName}` : 'Click to open player'}
      >
        {/* YouTube IFrame mounts here */}
        <div ref={ytWrapRef} style={{ width: '100%', height: '100%', pointerEvents: 'none' }} />

        {/* Gradient overlay with track name + play state */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none flex flex-col justify-end p-2">
          <div className="flex items-center gap-1.5">
            {isPlaying
              ? <Pause className="h-3 w-3 text-white/90 shrink-0" />
              : <Play  className="h-3 w-3 text-white/90 shrink-0" />}
            <span className="text-[10px] text-white/80 font-medium truncate">{trackName}</span>
          </div>
        </div>
      </div>

      {/* ── Collapsed music button (MP3 / synth only) ───────────────────
          Hidden when YouTube is active — the mini-player serves that role.
      ─────────────────────────────────────────────────────────────────── */}
      {!expanded && !isYtActive && (
        <button
          onClick={() => setExpanded(true)}
          className={`rounded-full bg-background/95 backdrop-blur border border-border/60 shadow-sm hover:shadow-md transition-all p-2 ${
            isPlaying ? 'text-foreground' : 'text-muted-foreground'
          }`}
          aria-label="Open ambient music player"
          title={isPlaying ? `Playing: ${trackName}` : 'Ambient music'}
        >
          <Music className={`h-4 w-4 ${isPlaying ? 'animate-pulse' : ''}`} />
        </button>
      )}
    </div>
  );
};

export default AmbientPlayer;
